import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { testimonials, brandStory } from "@/mocks/home";
import { aboutInfo } from "@/mocks/about";
import { communityStats } from "@/mocks/categories";

export default function AboutPage() {
  const { t } = useTranslation();

  return (
    <>
      <SEOHead
        title="关于我们——了解猫咪社区 | 猫咪社区"
        description="猫咪社区成立于2020年，是由一群热爱猫咪的资深铲屎官共同创立的猫咪爱好者平台。了解我们的品牌故事、用户口碑和联系方式。"
        keywords="猫咪社区,关于我们,品牌故事,联系猫咪社区"
        canonicalUrl="https://catcommunity.cn/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于猫咪社区",
            description: brandStory,
            about: {
              "@type": "Organization",
              name: aboutInfo.brandName,
              email: aboutInfo.email,
              address: {
                "@type": "PostalAddress",
                addressLocality: "北京",
                addressRegion: "北京市",
                addressCountry: "CN",
              },
            },
          }),
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: t("breadcrumb_home"), href: "/" },
              { label: t("about_title") },
            ]}
          />

          <div className="px-4 md:px-6 pb-14 md:pb-20">
            <section className="mb-16 md:mb-20">
              <div className="text-center mb-10">
                <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-4">
                  {t("about_brand")}
                </h1>
                <p className="text-foreground-500 text-sm md:text-base max-w-[600px] mx-auto">
                  {t("about_slogan")}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-xl font-heading text-foreground-900 mb-4">
                    {t("brand_story_title")}
                  </h2>
                  <div className="text-sm text-foreground-600 leading-relaxed space-y-4">
                    {brandStory.split("\n\n").map((para, index) => (
                      <p key={index}>{para}</p>
                    ))}
                  </div>
                </div>
                <div className="relative h-72 md:h-80 rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20cozy%20community%20space%20with%20several%20cats%20of%20different%20colors%20resting%20together%20on%20colorful%20cushions%2C%20soft%20afternoon%20sunlight%2C%20cream%20walls%20with%20cat-themed%20art%2C%20peaceful%20and%20welcoming%20atmosphere%2C%20warm%20tones%2C%20shallow%20depth%20of%20field&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="猫咪社区品牌故事"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
              </div>
            </section>

            <section className="mb-16 md:mb-20 bg-background-100 rounded-lg p-8 md:p-10">
              <h2 className="text-2xl font-heading text-foreground-900 mb-8 text-center">
                {t("about_stats")}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <span className="block text-3xl md:text-4xl font-heading text-primary-500 mb-2">
                    {aboutInfo.memberCount}
                  </span>
                  <span className="text-sm text-foreground-500">{t("member_count_label")}</span>
                </div>
                <div className="text-center">
                  <span className="block text-3xl md:text-4xl font-heading text-accent-500 mb-2">
                    {aboutInfo.articleCount}
                  </span>
                  <span className="text-sm text-foreground-500">{t("article_count_label")}</span>
                </div>
                <div className="text-center">
                  <span className="block text-3xl md:text-4xl font-heading text-primary-500 mb-2">
                    {aboutInfo.adoptionCount}
                  </span>
                  <span className="text-sm text-foreground-500">{t("adoption_count_label")}</span>
                </div>
                <div className="text-center">
                  <span className="block text-3xl md:text-4xl font-heading text-accent-500 mb-2">
                    {aboutInfo.foundedYear}
                  </span>
                  <span className="text-sm text-foreground-500">年成立</span>
                </div>
              </div>
            </section>

            <section className="mb-16 md:mb-20">
              <h2 className="text-2xl font-heading text-foreground-900 mb-8 text-center">
                {t("about_reputation")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {testimonials.map((item) => (
                  <div key={item.id} className="bg-background-100 rounded-lg p-6">
                    <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                      {item.content}
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-background-200/70">
                      <div className="w-10 h-10 rounded-full bg-accent-200 flex items-center justify-center text-accent-700 font-medium text-sm">
                        {item.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{item.name}</p>
                        <p className="text-xs text-foreground-400">{item.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-foreground-900 mb-8 text-center">
                {t("about_contact")}
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8 max-w-[500px] mx-auto">
                {t("about_contact_desc")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-[900px] mx-auto">
                <div className="bg-background-100 rounded-lg p-5 text-center">
                  <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <p className="text-xs text-foreground-400 mb-1">{t("contact_email")}</p>
                  <p className="text-sm font-medium text-foreground-800">{aboutInfo.email}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 text-center">
                  <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-wechat-line text-lg"></i>
                  </span>
                  <p className="text-xs text-foreground-400 mb-1">{t("contact_wechat")}</p>
                  <p className="text-sm font-medium text-foreground-800">{aboutInfo.wechat}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 text-center">
                  <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-weibo-line text-lg"></i>
                  </span>
                  <p className="text-xs text-foreground-400 mb-1">{t("contact_weibo")}</p>
                  <p className="text-sm font-medium text-foreground-800">{aboutInfo.weibo}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 text-center">
                  <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-map-pin-line text-lg"></i>
                  </span>
                  <p className="text-xs text-foreground-400 mb-1">{t("contact_address")}</p>
                  <p className="text-sm font-medium text-foreground-800">{aboutInfo.address}</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}